import type { ForecastPeriodType, ForecastReport } from '@finapp/types';
import { useCallback, useEffect, useState } from 'react';
import { ForecastApiError, forecastApi } from '../services/forecastApi';

interface UseForecastDataResult {
  forecastData: ForecastReport | null;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Loads the forecast report for a given year/as-of date/period scope.
 *
 * Note: follows the manual fetch-hook convention already used by useReportData.ts /
 * useTransactionData.ts (useState/useEffect, no caching/revalidation), rather than the
 * `swr` package - despite `swr` being listed in package.json, no hook in this codebase
 * currently uses it, so introducing it here alone would be an inconsistent one-off.
 */
const useForecastData = (
  year: number,
  asOfDate: string,
  periodType: ForecastPeriodType = 'ALL'
): UseForecastDataResult => {
  const [forecastData, setForecastData] = useState<ForecastReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadForecastData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await forecastApi.getForecast({ year, asOfDate, periodType });
      setForecastData(data);
    } catch (err) {
      if (err instanceof ForecastApiError) {
        setError(err.message);
      } else {
        setError('Failed to load forecast data. Please try again.');
      }
      console.error('Failed to load forecast data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [year, asOfDate, periodType]);

  useEffect(() => {
    loadForecastData();
  }, [loadForecastData]);

  return { forecastData, isLoading, error, reload: loadForecastData };
};

export default useForecastData;
